import React, { useState } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { patch } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';

const emptyAddress = () => ({
  streetAddress: '',
  addressLocality: '',
  addressRegion: '',
  postalCode: '',
  addressCountry: '',
});

function General({ website, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [areaInput, setAreaInput] = useState('');

  const jldSrc = website?.seoSettings?.jsonLDSettings;

  const [form, setForm] = useState({
    internal_domain: website?.internal_domain ?? '',
    title: website?.title ?? '',
    domain: website?.domain ?? '',
    description: website?.description ?? '',
    seoSettings: {
      showCookieConsent: website?.seoSettings?.showCookieConsent ?? false,
      googleAnalyticsId: website?.seoSettings?.googleAnalyticsId ?? '',
      jsonLDSettings: {
        profileType: jldSrc?.profileType ?? 'individual',
        id: jldSrc?.id ?? '',
        websiteId: jldSrc?.websiteId ?? '',
        name: jldSrc?.name ?? '',
        logo: jldSrc?.logo ?? '',
        jobTitle: jldSrc?.jobTitle ?? '',
        legalName: jldSrc?.legalName ?? '',
        foundingDate: jldSrc?.foundingDate ?? '',
        areaServed: Array.isArray(jldSrc?.areaServed) ? jldSrc.areaServed : [],
        address: Array.isArray(jldSrc?.address)
          ? jldSrc.address.map(a => ({
              streetAddress: a?.streetAddress ?? '',
              addressLocality: a?.addressLocality ?? '',
              addressRegion: a?.addressRegion ?? '',
              postalCode: a?.postalCode ?? '',
              addressCountry: a?.addressCountry ?? '',
            }))
          : [],
      },
    },
  });

  if (!website) {
    return <Alert variant="warning">Website not found.</Alert>;
  }

  const updateJLD = (field, value) => setForm(f => ({
    ...f,
    seoSettings: {
      ...f.seoSettings,
      jsonLDSettings: { ...f.seoSettings.jsonLDSettings, [field]: value },
    },
  }));

  const jld = () => form.seoSettings.jsonLDSettings;

  const addArea = () => {
    const val = areaInput.trim();
    if (!val) return;
    updateJLD('areaServed', [...jld().areaServed, val]);
    setAreaInput('');
  };

  const removeArea = (idx) => updateJLD('areaServed', jld().areaServed.filter((_, i) => i !== idx));

  const addAddress = () => updateJLD('address', [...jld().address, emptyAddress()]);

  const removeAddress = (idx) => updateJLD('address', jld().address.filter((_, i) => i !== idx));

  const updateAddressField = (idx, field, value) =>
    updateJLD('address', jld().address.map((a, i) => i === idx ? { ...a, [field]: value } : a));

  const isIndividual = form.seoSettings.jsonLDSettings.profileType === 'individual';

  const save = async (e) => {
    e.preventDefault();
    if (!form.internal_domain.trim() || !form.title.trim()) {
      toast.error('Internal domain and title are required');
      return;
    }
    try {
      setSaving(true);
      const jldVal = form.seoSettings.jsonLDSettings;
      const payload = {
        ...form,
        seoSettings: {
          ...form.seoSettings,
          googleAnalyticsId: form.seoSettings.googleAnalyticsId.trim() || null,
          jsonLDSettings: {
            profileType: jldVal.profileType,
            id: jldVal.id.trim() || null,
            websiteId: jldVal.websiteId.trim() || null,
            name: jldVal.name.trim(),
            logo: jldVal.logo.trim() || null,
            jobTitle: jldVal.jobTitle.trim() || null,
            legalName: jldVal.legalName.trim() || null,
            foundingDate: jldVal.foundingDate.trim() || null,
            areaServed: jldVal.areaServed.filter(v => v.trim() !== ''),
            address: jldVal.address
              .map(a => ({
                streetAddress: a.streetAddress.trim() || null,
                addressLocality: a.addressLocality.trim() || null,
                addressRegion: a.addressRegion.trim() || null,
                postalCode: a.postalCode.trim() || null,
                addressCountry: a.addressCountry.trim() || null,
              }))
              .filter(a => Object.values(a).some(v => v !== null)),
          },
        },
      };
      const resp = await patch(`websites/${website.id}`, payload);
      const updated = resp?.website
        ? { ...website, ...resp.website, seoSettings: { ...website.seoSettings, ...resp.website.seoSettings, jsonLDSettings: resp.website.seoSettings?.jsonLDSettings ?? payload.seoSettings.jsonLDSettings } }
        : { ...website, ...payload };
      onUpdated?.(updated);
    } catch (e) {
      toast.error('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Form onSubmit={save}>
        <Form.Group className="mb-3 form-row-horizontal" controlId="editInternalDomain">
          <Form.Label>Internal domain</Form.Label>
          <Form.Control
            type="text"
            value={form.internal_domain}
            onChange={(e) => setForm({ ...form, internal_domain: e.target.value })}
            placeholder="e.g. project-123"
          />
        </Form.Group>
        <Form.Group className="mb-3 form-row-horizontal" controlId="editTitle">
          <Form.Label>Title</Form.Label>
          <Form.Control
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Marketing site"
          />
        </Form.Group>
        <Form.Group className="mb-3 form-row-horizontal" controlId="editDomain">
          <Form.Label>Domain</Form.Label>
          <Form.Control
            type="text"
            value={form.domain}
            onChange={(e) => setForm({ ...form, domain: e.target.value })}
            placeholder="Optional: www.example.com"
          />
        </Form.Group>
        <Form.Group className="mb-3 form-row-horizontal" controlId="editDescription">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Optional: brief description of the website"
          />
        </Form.Group>

        <hr />
        <h6 className="mb-3">SEO Settings</h6>
        <Form.Group className="mb-3 form-row-horizontal" controlId="editShowCookieConsent">
          <Form.Check
            type="checkbox"
            label="Show cookie consent"
            checked={form.seoSettings.showCookieConsent}
            onChange={(e) => setForm({ ...form, seoSettings: { ...form.seoSettings, showCookieConsent: e.target.checked } })}
          />
        </Form.Group>
        {form.seoSettings.showCookieConsent && (
          <Form.Group className="mb-3 form-row-horizontal" controlId="editGoogleAnalyticsId">
            <Form.Label>Google Analytics ID</Form.Label>
            <Form.Control
              type="text"
              value={form.seoSettings.googleAnalyticsId}
              onChange={(e) => setForm({ ...form, seoSettings: { ...form.seoSettings, googleAnalyticsId: e.target.value } })}
              placeholder="e.g. G-XXXXXXXXXX"
            />
          </Form.Group>
        )}

        <hr />
        <h6 className="mb-3">Structured Data (JSON-LD) <a href="https://search.google.com/test/rich-results" target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem', fontWeight: 400 }}>Test</a></h6>
        <Form.Group className="mb-3 form-row-horizontal" controlId="editJldWebsiteId">
          <Form.Label>Website ID</Form.Label>
          <Form.Control
            type="url"
            value={form.seoSettings.jsonLDSettings.websiteId}
            onChange={(e) => updateJLD('websiteId', e.target.value)}
            placeholder="https://example.com/#website"
          />
        </Form.Group>
        <Form.Group className="mb-3 form-row-horizontal" controlId="editJldId">
          <Form.Label>Publisher ID</Form.Label>
          <Form.Control
            type="url"
            value={form.seoSettings.jsonLDSettings.id}
            onChange={(e) => updateJLD('id', e.target.value)}
            placeholder="https://example.com/#person"
          />
        </Form.Group>
        <Form.Group className="mb-3 form-row-horizontal" controlId="editProfileType">
          <Form.Label>Profile type</Form.Label>
          <Form.Select
            value={form.seoSettings.jsonLDSettings.profileType}
            onChange={(e) => updateJLD('profileType', e.target.value)}
          >
            <option value="individual">Individual</option>
            <option value="organisation">Organisation</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3 form-row-horizontal" controlId="editJldName">
          <Form.Label>{isIndividual ? 'Person name' : 'Org name'}</Form.Label>
          <Form.Control
            type="text"
            value={form.seoSettings.jsonLDSettings.name}
            onChange={(e) => updateJLD('name', e.target.value)}
            placeholder={isIndividual ? 'e.g. Jane Smith' : 'e.g. Acme Corp'}
          />
        </Form.Group>
        <Form.Group className="mb-3 form-row-horizontal" controlId="editJldLogo">
          <Form.Label>{isIndividual ? 'Photo URL' : 'Logo URL'}</Form.Label>
          <Form.Control
            type="url"
            value={form.seoSettings.jsonLDSettings.logo}
            onChange={(e) => updateJLD('logo', e.target.value)}
            placeholder="https://example.com/photo.jpg"
          />
        </Form.Group>

        {isIndividual && (
          <Form.Group className="mb-3 form-row-horizontal" controlId="editJldJobTitle">
            <Form.Label>Job title</Form.Label>
            <Form.Control
              type="text"
              value={form.seoSettings.jsonLDSettings.jobTitle}
              onChange={(e) => updateJLD('jobTitle', e.target.value)}
              placeholder="e.g. Freelance Photographer"
            />
          </Form.Group>
        )}

        {!isIndividual && (
          <>
            <Form.Group className="mb-3 form-row-horizontal" controlId="editJldLegalName">
              <Form.Label>Legal name</Form.Label>
              <Form.Control
                type="text"
                value={form.seoSettings.jsonLDSettings.legalName}
                onChange={(e) => updateJLD('legalName', e.target.value)}
                placeholder="Registered legal name"
              />
            </Form.Group>
            <Form.Group className="mb-3 form-row-horizontal" controlId="editJldFoundingDate">
              <Form.Label>Founded</Form.Label>
              <Form.Control
                type="text"
                value={form.seoSettings.jsonLDSettings.foundingDate}
                onChange={(e) => updateJLD('foundingDate', e.target.value)}
                placeholder="e.g. 2015"
              />
            </Form.Group>
          </>
        )}

        {/* Area served — tag input */}
        {!isIndividual && <div className="mb-3 form-row-horizontal">
          <Form.Label>Area served</Form.Label>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <Form.Control
                type="text"
                value={areaInput}
                onChange={(e) => setAreaInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addArea(); } }}
                placeholder="e.g. Poland"
              />
              <button type="button" className="structure-add-child-btn" onClick={addArea} style={{ whiteSpace: 'nowrap' }}>+ Add</button>
            </div>
            {form.seoSettings.jsonLDSettings.areaServed.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {form.seoSettings.jsonLDSettings.areaServed.map((area, idx) => (
                  <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(49,59,114,0.09)', borderRadius: 20, padding: '2px 10px', fontSize: '0.82rem', fontWeight: 600, color: 'var(--navy)' }}>
                    {area}
                    <button type="button" onClick={() => removeArea(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--navy)', padding: 0, lineHeight: 1, opacity: 0.5, fontSize: '0.9rem' }}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>}

        {/* Addresses */}
        <div className="mb-3">
          <div className="structure-header-row mb-2">
            <span style={{ width: 140, fontSize: '1rem' }}>Addresses</span>
            <div className="structure-controls">
              <button type="button" className="structure-add-child-btn" onClick={addAddress}>+ Add address</button>
            </div>
          </div>
          {form.seoSettings.jsonLDSettings.address.map((addr, idx) => (
            <div key={idx} className="structure-node mb-2" style={{ border: '1px solid rgba(49,59,114,0.15)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span className="structure-type-label">Address {idx + 1}</span>
                <button type="button" className="structure-delete-btn" title="Remove" onClick={() => removeAddress(idx)}>×</button>
              </div>
              {[
                { field: 'streetAddress', label: 'Street', placeholder: '123 Main St' },
                { field: 'addressLocality', label: 'City', placeholder: 'Warsaw' },
                { field: 'addressRegion', label: 'Region', placeholder: 'Masovian' },
                { field: 'postalCode', label: 'Postal code', placeholder: '00-001' },
                { field: 'addressCountry', label: 'Country', placeholder: 'PL' },
              ].map(({ field, label, placeholder }) => (
                <Form.Group key={field} className="mb-2 form-row-horizontal">
                  <Form.Label style={{ width: 100, minWidth: 100, fontSize: '0.85rem' }}>{label}</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={addr[field]}
                    onChange={(e) => updateAddressField(idx, field, e.target.value)}
                    placeholder={placeholder}
                  />
                </Form.Group>
              ))}
            </div>
          ))}
        </div>

        <div className="form-actions">
          <Button type="submit" className="primary-button" disabled={saving}>
            {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default General;
